
import React from "react";
import { Search, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface FreelancerSearchProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  availableFreelancers: any[];
  addToTeam: (freelancer: any) => void;
}

export const FreelancerSearch: React.FC<FreelancerSearchProps> = ({
  searchQuery,
  setSearchQuery,
  availableFreelancers,
  addToTeam
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5 text-indigo-500" />
          Find Team Members
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
          <Input
            placeholder="Search by name or skill..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="grid gap-4">
          {availableFreelancers.length > 0 ? (
            availableFreelancers.map((freelancer) => (
              <div
                key={freelancer.id}
                className="grid sm:grid-cols-[auto_1fr_auto] gap-4 p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Avatar className="h-12 w-12">
                  <AvatarImage src={freelancer.avatar} />
                  <AvatarFallback>{freelancer.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-medium flex items-center gap-2">
                    {freelancer.name}
                    {freelancer.isVerified && (
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                    )}
                  </div>
                  <div className="text-sm text-gray-500 mb-2">{freelancer.title}</div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {freelancer.skills.slice(0, 3).map((skill, i) => (
                      <Badge key={i} variant="outline" className="bg-indigo-50">
                        {skill}
                      </Badge>
                    ))}
                    {freelancer.skills.length > 3 && (
                      <Badge variant="outline">+{freelancer.skills.length - 3}</Badge>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 flex items-center">
                    <span className="flex items-center mr-3">
                      Rating: {freelancer.rating}/5
                    </span>
                    <span>${freelancer.dailyRate}/day</span>
                  </div>
                </div>
                <div className="flex items-center">
                  <Button 
                    onClick={() => addToTeam(freelancer)} 
                    className="bg-indigo-500 hover:bg-indigo-600"
                  >
                    Add to Team
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center p-6 text-gray-500">
              <Search className="h-10 w-10 mx-auto mb-4 text-gray-300" />
              <p>No matching freelancers found.</p>
              <p className="text-sm mt-1">Try adjusting your search criteria.</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
